import { useMemo, useState } from "react";
import { motion } from "framer-motion";
import { ArrowRight, ClipboardList, Clock, TrendingUp, Users } from "lucide-react";
import { Link } from "react-router-dom";
import Layout from "../components/Layout";
import { currency } from "../lib/api";
import { STATES_META, salesForState, summariseSales } from "../data/recentSales";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";

export default function RecentSales() {
  const [stateCode, setStateCode] = useState("NSW");
  const meta = useMemo(
    () => STATES_META.find((s) => s.code === stateCode),
    [stateCode],
  );
  const rows = useMemo(() => salesForState(stateCode), [stateCode]);
  const summary = useMemo(() => summariseSales(rows), [rows]);

  return (
    <Layout>
      {/* Hero image */}
      <section className="relative h-64 w-full overflow-hidden sm:h-80">
        <img
          src={meta.image}
          alt={`${meta.label} gym interior`}
          className="h-full w-full object-cover"
          data-testid="state-hero-image"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-slate-900/85 via-slate-900/40 to-transparent" />
        <div className="absolute inset-x-0 bottom-0">
          <div className="mx-auto max-w-6xl px-6 pb-8">
            <div className="text-xs font-bold uppercase tracking-[0.2em] text-orange-300">
              Recent sales
            </div>
            <h1 className="mt-2 font-display text-4xl font-extrabold leading-tight text-white sm:text-5xl">
              {meta.label}
            </h1>
          </div>
        </div>
      </section>

      <section className="mx-auto max-w-6xl px-6 py-10">
        {/* State selector */}
        <div className="mb-8 flex flex-col items-start justify-between gap-4 sm:flex-row sm:items-center">
          <div>
            <div className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-500">
              Select state
            </div>
            <div className="mt-1 max-w-sm">
              <Select value={stateCode} onValueChange={setStateCode}>
                <SelectTrigger className="h-12 w-64" data-testid="state-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {STATES_META.map((s) => (
                    <SelectItem key={s.code} value={s.code} data-testid={`state-opt-${s.code}`}>
                      {s.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <Link to="/valuation" className="btn-primary" data-testid="recent-sales-cta">
            Value my gym <ArrowRight className="h-4 w-4" />
          </Link>
        </div>

        {/* Summary */}
        {summary ? (
          <motion.div
            key={stateCode}
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3 }}
            className="rounded-2xl border border-slate-200 bg-white p-6 sm:p-8"
            data-testid="state-summary"
          >
            <div className="mb-4 flex items-center gap-2">
              <ClipboardList className="h-4 w-4 text-orange-500" />
              <span className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-500">
                {meta.label} — {summary.count} recorded sales
              </span>
            </div>
            <p className="text-sm leading-relaxed text-slate-700">
              We have sold <strong className="text-slate-900">{summary.count}</strong> gyms and
              studios in {meta.label}, with an <strong>average sale price of {currency(summary.avg_price)}</strong>
              {" "}(median {currency(summary.median_price)}). Businesses attracted an average of
              {" "}<strong>{summary.avg_buyers} interested buyers</strong> and were on market for
              {" "}<strong>{summary.avg_dom} days</strong> on average. The highest sale was
              {" "}<strong>{currency(summary.highest)}</strong> and the lowest was {currency(summary.lowest)}.
            </p>
            <div className="mt-6 grid grid-cols-2 gap-4 sm:grid-cols-4">
              <SummaryTile icon={ClipboardList} label="Total sales" value={summary.count} />
              <SummaryTile icon={TrendingUp} label="Avg sale price" value={currency(summary.avg_price)} />
              <SummaryTile icon={Clock} label="Avg days on market" value={`${summary.avg_dom}`} />
              <SummaryTile icon={Users} label="Avg buyers" value={summary.avg_buyers} />
            </div>
          </motion.div>
        ) : null}

        {/* Sales table */}
        <div className="mt-8 overflow-x-auto rounded-2xl border border-slate-200 bg-white">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-slate-50 text-left text-xs uppercase tracking-wider text-slate-500">
                <th className="px-4 py-3">Sold price</th>
                <th className="px-4 py-3">Days on market</th>
                <th className="px-4 py-3">Interested buyers</th>
                <th className="px-4 py-3">State</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100" data-testid="sales-tbody">
              {rows
                .slice()
                .sort((a, b) => b.sold_price - a.sold_price)
                .map((r, i) => (
                  <tr key={i} className="hover:bg-slate-50">
                    <td className="px-4 py-3 font-semibold text-slate-900">{currency(r.sold_price)}</td>
                    <td className="px-4 py-3 text-slate-700">{r.days_on_market}</td>
                    <td className="px-4 py-3 text-slate-700">{r.interested_buyers}</td>
                    <td className="px-4 py-3 text-slate-500">{r.state}</td>
                  </tr>
                ))}
            </tbody>
          </table>
        </div>

        <p className="mt-6 text-xs text-slate-500">
          Data source: Bonza Business &amp; Franchise Sales — completed gym and
          studio transactions. Individual results are indicative of the range of
          outcomes we have delivered; each business is different.
        </p>
      </section>
    </Layout>
  );
}

function SummaryTile({ icon: Icon, label, value }) {
  return (
    <div className="rounded-xl border border-slate-200 bg-slate-50 p-4">
      <div className="flex items-center gap-2 text-orange-600">
        <Icon className="h-4 w-4" />
        <span className="text-[10px] font-bold uppercase tracking-wider text-slate-500">{label}</span>
      </div>
      <div className="mt-2 font-display text-xl font-extrabold text-slate-900">{value}</div>
    </div>
  );
}
